import { Permissions } from '@core/client/permissions';

export enum ManageView {
    Diary = 0,
    Summary = 1,
    Search = 2,
    Workflow = 3
};

export interface RootNavigation {
    name?: string;
    permissions?: number[];
    negativePermissions?: number[];
    url?: string;
    visible?: boolean;
    svgIconClassName?: string; // Added for icons
    children?: ChildNavigation[];
}

export interface ChildNavigation {
    name?: string;
    permissions?: number[];
    url?: string;
    visible?: boolean;
    isFragment?: boolean;
    svgIconClassName?: string; // Added for icons
}

export const userNavigation: ChildNavigation[] = [
    {
        name: 'Users',
        url: '/settings/users'
    },
    {
        name: 'Interviewers',
        url: '/settings/users/interviewers'
    },
    {
        name: 'Authorisers',
        url: '/settings/users/authorisers'
    }
];

export const organisationNavigation: ChildNavigation[] = [
    {
        name: 'Details',
        url: 'details'
    },
    {
        name: 'Users',
        url: 'users'
    },
    {
        name: 'Locations',
        url: 'locations'
    },
    {
        name: 'Audit',
        url: 'audit'
    },
];

export const vacancyNavigation: ChildNavigation[] = [
    {
        name: 'Details',
        url: 'details'
    },
    {
        name: 'Application Form',
        url: 'application-form'
    },
    {
        name: 'Workflow',
        url: 'workflow'
    },
    {
        name: 'Job Description',
        url: 'job-description'
    },
    {
        name: 'NHS Jobs',
        url: 'nhs-jobs'
    },
    {
        name: 'Questions',
        url: 'questions'
    }, {
        name: 'Advertising',
        url: 'advertising'
    },
    {
        name: 'Agencies',
        url: 'agencies',
        permissions: [Permissions.VacanciesPSL]
    },
    {
        name: 'Interviews',
        url: 'interviews'
    },
    {
        name: 'AI',
        url: 'ai'
    }
];

export const atsNavigation: RootNavigation[] = [
    {
        name: 'Dashboard',
        url: '/dashboard',
        children: []
    },
    {
        name: 'Diary',
        url: '/diary',
        visible: false,
        children: [
            {
                name: 'My Diary',
                url: '/diary/mine'
            }, {
                name: 'System Diary',
                url: '/diary/all',
                permissions: [Permissions.SystemCalendar]
            }
        ]
    },
    {
        name: 'Manage',
        url: '/manage',
        permissions: [
            Permissions.Applications,
            Permissions.ApplicationProgress
        ],
        children: [{
            name: 'Diary',
            url: 'diary',
            isFragment: true,
            svgIconClassName: 'svnIcon--diary',
        },
        {
            name: 'Summary',
            url: 'summary',
            isFragment: true,
            svgIconClassName: 'svnIcon--summary',
        },
        {
            name: 'Search',
            url: 'search',
            isFragment: true,
        },
        {
            name: 'Workflow',
            url: 'workflow',
            isFragment: true,
            svgIconClassName: 'svnIcon--workflow',
        },
        ]
    },
    {
        name: 'Vacancies',
        url: '/vacancies',
        permissions: [
            Permissions.Vacancies,
            Permissions.VacanciesCreate,
            Permissions.VacanciesEdit,
            Permissions.VacanciesTemplate,
            Permissions.VacanciesView
        ],
        children: [
            {
                name: 'Search',
                url: '/vacancies/search',
                permissions: [
                    Permissions.VacanciesView,
                    Permissions.VacanciesEdit,
                    Permissions.VacanciesCreate
                ]
            },
            {
                name: 'Create',
                url: 'create',
                isFragment: true,
                permissions: [
                    Permissions.VacanciesCreate,
                    Permissions.VacanciesTemplate,
                    Permissions.VacanciesCreateFromTemplate
                ]
            },
            {
                name: 'View',
                url: '/vacancies/view',
                permissions: [Permissions.VacanciesView],
                visible: true
            },
            {
                name: 'Templates',
                url: '/vacancies/templates',
                permissions: [Permissions.VacanciesTemplate]
            }
        ]
    },
    {
        name: 'Vacancy Settings',
        url: '/job-areas',
        permissions: [
            Permissions.Assessment,
            Permissions.VacancyInterviews,
            Permissions.VacancyInterviews,
            Permissions.Onboarding,
            Permissions.VacancyProcess],
        children: [
            {
                name: 'Assessments',
                url: '/job-areas/assessments',
                permissions: [Permissions.Assessment]
            },
            {
                name: 'Interviews',
                url: '/job-areas/interviews',
                permissions: [Permissions.VacancyInterviews]
            },
            {
                name: 'Scoring',
                url: '/job-areas/scoring',
                permissions: [Permissions.VacancyInterviews]
            },
            {
                name: 'Onboarding',
                url: '/job-areas/onboarding',
                permissions: [Permissions.Onboarding]
            },
            {
                name: 'Workflow Stages',
                url: '/job-areas/workflows/stages',
                permissions: [Permissions.VacancyProcess]
            },
            {
                name: 'Workflows',
                url: '/job-areas/workflows/processes',
                permissions: [Permissions.VacancyProcess]
            },
        ]
    },
    {
        name: 'Talent',
        url: '/talent',
        permissions: [Permissions.Talent],
        negativePermissions: [Permissions.ApplicationAnonymous],
        children: [
            {
                name: 'Talent Search',
                url: '/talent/search'
            },
            {
                name: 'Talent Management',
                url: '/talent/management'
            },
            {
                name: 'Applicant Search',
                url: '/talent/applications'
            }
        ],
    },
    {
        name: 'Organisations',
        url: '/organisations',
        permissions: [Permissions.Organisations, Permissions.OrganisationUser],
        children: [
            {
                name: 'View',
                url: '/organisations/view',
            },
            {
                name: 'Search',
                url: '/organisations/search',
                permissions: [Permissions.Organisations]
            },
            {
                name: 'Create',
                url: '/organisations/create/0',
                permissions: [Permissions.Organisations]
            },            
        ]
    },
    {
        name: 'Reports',
        url: '/reports',
        permissions: [
            Permissions.Reports,
            Permissions.ReportsATS,
            Permissions.ReportsGA
        ],
        children: [
            {
                name: 'CSV',
                url: '/reports/csv'
            },
            {
                name: 'Charts',
                url: '/reports/charts'
            },
        ]
    },
    {
        name: 'System',
        url: '/settings',
        children: [
            {
                name: 'Emails',
                url: '/settings/emails',
                permissions: [Permissions.SystemEmails]
            },
            {
                name: 'SMS',
                url: '/settings/sms',
                permissions: [Permissions.SystemEmails]
            },            {
                name: 'Agencies',
                url: '/settings/agents',
                permissions: [Permissions.SystemAgent]
            },
            {
                name: 'Profile Templates',
                url: '/settings/profile-templates',
                permissions: [Permissions.SystemBrands] // todo
            },
            {
                name: 'Locations',
                url: '/settings/locations',
                permissions: [Permissions.SystemLocations]
            },
            {
                name: 'Sectors',
                url: '/settings/sectors',
                permissions: [Permissions.SystemBusinessAreas]
            },
            {
                name: 'Departments',
                url: '/settings/departments',
                permissions: [Permissions.SystemBusinessAreas]
            },
            {
                name: 'Grades',
                url: '/settings/grades',
                permissions: [Permissions.SystemRewards]
            },
            {
                name: 'Brands',
                url: '/settings/brands',
                permissions: [Permissions.SystemBrands]
            },
            {
                name: 'Business Areas',
                url: '/settings/business-areas',
                permissions: [Permissions.SystemBusinessAreas]
            },
            {
                name: 'Qualities',
                url: '/settings/qualities',
                permissions: [Permissions.SystemQualites]
            },
            {
                name: 'AI',
                url: '/settings/ai',
                permissions: [Permissions.SystemBusinessAreas]
            },
            {
                name: 'System Rights',
                url: '/settings/system-rights',
                permissions: [Permissions.FullSystemRights]
            },
            {
                name: 'Users',
                url: '/settings/users',
                permissions: [Permissions.SystemUsers]
            },
            {
                name: 'My Settings',
                url: '/settings/my-settings'
            },
        ]
    }
];
